"use client";

import { useCallback, useSyncExternalStore } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "dubu-pro:theme";

/**
 * Runs before first paint (see <ThemeScript/>) to stamp data-theme on <html>.
 * Without this the page paints in the default theme and then flips: the classic
 * dark-mode flash. Kept dependency-free and inlined for that reason.
 */
export const themeInitScript = `
(function(){
  try {
    var s = localStorage.getItem('${STORAGE_KEY}');
    var t = s === 'light' || s === 'dark'
      ? s
      : (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
    document.documentElement.dataset.theme = t;
  } catch (e) {
    document.documentElement.dataset.theme = 'dark';
  }
})();
`;

export const ThemeScript = () => (
  <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
);

/*
 * The <html data-theme> attribute IS the source of truth: the init script sets it before
 * React exists, and CSS reads it directly. So the theme is an external store React
 * subscribes to, not React state that must be re-synced into the DOM. Modelling it that way
 * removes the mount-time setState (and its extra render) that a useState+useEffect version
 * needs purely to catch up with what the DOM already knows.
 */
const listeners = new Set<() => void>();
let snapshot: Theme | null = null;

const subscribe = (onChange: () => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

const getSnapshot = (): Theme => {
  if (snapshot) return snapshot;
  const attr = document.documentElement.dataset.theme;
  snapshot = attr === "light" ? "light" : "dark";
  return snapshot;
};

// SSR has no DOM to read; the init script corrects the client before paint, and
// suppressHydrationWarning on <html> covers the attribute mismatch.
const getServerSnapshot = (): Theme => "dark";

export const setTheme = (t: Theme) => {
  snapshot = t;
  document.documentElement.dataset.theme = t;
  try {
    localStorage.setItem(STORAGE_KEY, t);
  } catch {
    // Private mode / storage disabled, theme still applies for this session.
  }
  listeners.forEach((l) => l());
};

export const useTheme = () => {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const toggleTheme = useCallback(
    () => setTheme(theme === "dark" ? "light" : "dark"),
    [theme]
  );
  return { theme, setTheme, toggleTheme };
};

/**
 * Read a themed token as a concrete color for consumers that can't use CSS vars.
 * Lightweight Charts paints to canvas and needs real values, not var() references,
 * so the chart re-reads these whenever the theme or user prefs change.
 */
export const readToken = (name: string, fallback = "#000"): string => {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return fallback;
  // Tokens are stored as bare HSL triplets so they can compose with alpha.
  return raw.startsWith("#") || raw.startsWith("rgb") || raw.startsWith("hsl")
    ? raw
    : `hsl(${raw})`;
};

/**
 * Resolve a token to a comma-form hsla() string with optional alpha: REQUIRED for GSAP.
 *
 * GSAP parses color strings with a number-extracting regex; `hsl(var(--buy) / 0.22)` contains
 * no digits, the match returns null, and GSAP crashes with "Cannot read properties of null
 * (reading 'map')", rethrown by its ticker on every animation frame (the production crash of
 * 2026-07-18, 4500+ repeating errors). CSS can defer var() resolution; GSAP cannot. Anything
 * animated by GSAP must go through this (or readToken), never a var() expression.
 */
export const tokenHsla = (name: string, alpha = 1, fallback = "#000"): string => {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  // Expect a bare triplet like "137 78% 36%".
  const m = raw.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!m) return raw || fallback; // already-concrete color (or missing), pass through
  return alpha >= 1
    ? `hsl(${m[1]}, ${m[2]}%, ${m[3]}%)`
    : `hsla(${m[1]}, ${m[2]}%, ${m[3]}%, ${alpha})`;
};
